import { NinjaArt } from "../art/NinjaArt";
import { SetDots } from "../components/SetDots";
import { SlotRow } from "../components/SlotRow";
import { Stripes } from "../components/Stripes";
import { MODE_INFO, type Mode } from "../content/modes";
import { ninjaOfRank } from "../content/ninjas";
import { RANKS } from "../content/ranks";
import { variantName } from "../content/variants";
import { isKnown } from "../game/roster";
import { frameFor, setProgress } from "../game/sets";
import { cardStripes } from "../game/stripes";
import { trialFor, trialLock } from "../game/trials";
import type { SaveState } from "../save/schema";
import "./Ladder.css";

interface Props {
  save: SaveState;
  mode: Mode;
  onOpenCard: (rankIndex: number) => void;
  onBack: () => void;
}

export function Ladder({ save, mode, onOpenCard, onBack }: Props) {
  const here = save.rank[mode];
  const trials = save.trials[mode] ?? {};
  const rows = RANKS.map((r, i) => ({ r, i })).reverse();
  const multi = setProgress(save.rank, save.settings.ops, 0).length > 1;
  // Strands climb together: the next card is locked until the others catch up.
  const lock = trialLock(save.rank, save.settings.ops, mode);
  const lockNote = lock
    ? `locked until ${lock.behind.map((op) => MODE_INFO[op].label).join(", ")} reach ${RANKS[lock.rankIndex]!.name}`
    : null;
  return (
    <section className="screen ladder-screen">
      <h2 className="display ladder-title">
        Ninja ranks ({MODE_INFO[mode].label}
        {mode === "add" ? "" : `: ${variantName(mode, "").trim()} ninjas`})
      </h2>
      <ul className="ladder">
        {rows.map(({ r, i }) => {
          const locked = i > here;
          const known = isKnown(save.rank, save.roster, ninjaOfRank(i));
          const spec = trialFor(mode, i);
          const passed = !!trials[String(i)]?.passed;
          const frame = frameFor(save.sets, i);
          // Stripes show on the current card, counting towards the next trial.
          const stripes =
            i === here && i < RANKS.length - 1 ? cardStripes(save.stripes, mode, here) : null;
          const name = variantName(mode, r.name);
          const strandLocked = i === here + 1 && lockNote !== null;
          return (
            <li key={r.name}>
              <button
                type="button"
                className={[
                  "ladder-row",
                  locked ? "locked" : "",
                  i === here ? "here" : "",
                  frame ? `frame-${frame}` : "",
                ]
                  .join(" ")
                  .replace(/\s+/g, " ")
                  .trim()}
                style={{ "--c": r.colour } as React.CSSProperties}
                aria-label={
                  (locked && !known
                    ? `Mystery ninja, rank ${i + 1}, pass mark ${spec?.passMark}`
                    : `${name}, rank ${i + 1}${locked ? `, pass mark ${spec?.passMark}` : ""}${passed ? ", trial passed" : ""}`) +
                  (frame ? `, ${frame} card` : "") +
                  (stripes !== null ? `, ${stripes} of 3 stripes` : "") +
                  (strandLocked ? `, ${lockNote}` : "")
                }
                onClick={() => onOpenCard(i)}
              >
                <NinjaArt
                  rank={r}
                  variant={mode}
                  locked={locked}
                  stripes={i === here ? (stripes ?? 0) : 0}
                  className="ladder-art"
                />
                <span className="ladder-name display">
                  {locked && !known ? "???" : name}
                  {strandLocked && (
                    <span className="lock-tag ladder-lock" aria-hidden="true">
                      Locked
                    </span>
                  )}
                </span>
                <span className="ladder-score">
                  {spec === null ? "Start" : `Pass mark ${spec.passMark}`}
                  {passed && (
                    <span className="tick" aria-hidden="true">
                      {" "}
                      ✓
                    </span>
                  )}
                </span>
                <span className="ladder-meta" aria-hidden="true">
                  <SlotRow ninja={ninjaOfRank(i)} locked className="ladder-slots" />
                </span>
                {(stripes !== null || (multi && i > 0)) && (
                  <span className="ladder-meta" aria-hidden="true">
                    {stripes !== null && <Stripes count={stripes} className="ladder-stripes" />}
                    {multi && i > 0 && (
                      <SetDots
                        dots={setProgress(save.rank, save.settings.ops, i)}
                        className="ladder-dots"
                      />
                    )}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      <button type="button" className="big display ladder-back" onClick={onBack}>
        Back
      </button>
    </section>
  );
}
